import type { Ref } from 'vue'

export interface ApiStateOptions {
  /** Ref passé à true pendant l'appel, remis à false dans le finally. */
  loading?: Ref<boolean>
  /** Ref d'erreur : remis à null avant l'appel, positionné à `errorText` en cas d'échec. */
  error?: Ref<string | null>
  /** Message stocké dans `error` en cas d'échec (si `error` est fourni). */
  errorText?: string
  /** Libellé passé en premier argument de console.error (avec l'erreur). Par défaut : l'erreur seule. */
  logLabel?: string
  /** Relancer l'erreur après l'avoir loggée/stockée (par défaut false : l'erreur est avalée). */
  rethrow?: boolean
}

/**
 * Factorise le boilerplate try/catch/finally des actions de store :
 * bascule `loading`, réinitialise puis positionne `error`, loggue, et relance au besoin.
 * Le comportement (rethrow, set d'erreur, libellé de log) est paramétrable pour rester
 * iso-fonctionnel avec le code d'origine.
 *
 * Avec `rethrow: true`, l'erreur est relancée : la fonction ne peut donc renvoyer que `T`.
 * Sans `rethrow`, l'erreur est avalée et la fonction renvoie `T | undefined`.
 */
export async function withApiState<T>(fn: () => Promise<T>, opts: ApiStateOptions & { rethrow: true }): Promise<T>
export async function withApiState<T>(fn: () => Promise<T>, opts?: ApiStateOptions): Promise<T | undefined>
export async function withApiState<T>(fn: () => Promise<T>, opts: ApiStateOptions = {}): Promise<T | undefined> {
  if (opts.loading) opts.loading.value = true
  if (opts.error) opts.error.value = null
  try {
    return await fn()
  } catch (err) {
    if (opts.logLabel) console.error(opts.logLabel, err)
    else console.error(err)
    if (opts.errorText !== undefined && opts.error) opts.error.value = opts.errorText
    if (opts.rethrow) throw err
    return undefined
  } finally {
    if (opts.loading) opts.loading.value = false
  }
}
